import { useFormikContext } from 'formik';
import React, { useEffect, useMemo, useRef } from 'react';
import { Mansion } from '../../store/mansionsSlice';
import useTranslate from '../useTranslate';

export interface MansionAssignments {
    mansionIds: number[];
}

interface Props {
    idToMansion: { [key: string]: Mansion };
    disabled?: boolean;
}

function OperationalContactAssignmentsForm(props: Readonly<Props>) {
    const { values, setFieldValue } = useFormikContext<MansionAssignments>();
    const strings = useTranslate('OperationalContactAssign');

    const allIds = useMemo(
        () => Object.keys(props.idToMansion).map(Number),
        [props.idToMansion]
    );
    const selectedIds = values.mansionIds ?? [];

    const allSelected = values.mansionIds.length === allIds.length;
    const noneSelected = values.mansionIds.length === 0;
    const partiallySelected = !noneSelected && !allSelected;

    const checkboxAllRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (checkboxAllRef.current) {
            checkboxAllRef.current.indeterminate = partiallySelected;
        }
    }, [partiallySelected]);

    return (
        <table className="data-table" style={{ width: '100%' }}>
            <thead>
                <tr>
                    <th>
                        <div
                            className={
                                'checkbox checkbox-primary checkbox-custom'
                            }
                        >
                            <input
                                ref={checkboxAllRef}
                                type={'checkbox'}
                                className={'styled'}
                                id={'checkbox-all'}
                                checked={allSelected}
                                onChange={toggleAll}
                                disabled={props.disabled}
                            />
                            <label
                                htmlFor={'checkbox-all'}
                                aria-label={strings.checkAll}
                            />
                        </div>
                    </th>
                    <th>{strings.street}</th>
                    <th>{strings.city}</th>
                    <th>{strings.countLocations}</th>
                </tr>
            </thead>
            <tbody>
                {Object.values(props.idToMansion).map((mansion) => {
                    const checked = selectedIds.includes(mansion.id);
                    return (
                        <tr key={mansion.id}>
                            <td>
                                <div
                                    className={
                                        'checkbox checkbox-primary checkbox-custom'
                                    }
                                >
                                    <input
                                        type={'checkbox'}
                                        id={`checkbox-${mansion.id}`}
                                        className={'styled'}
                                        checked={checked}
                                        onChange={() => toggleOne(mansion.id)}
                                        disabled={props.disabled}
                                    />
                                    <label
                                        htmlFor={`checkbox-${mansion.id}`}
                                        aria-label={strings.checkOneAria}
                                    />
                                </div>
                            </td>
                            <td>
                                {mansion.address.street}{' '}
                                {mansion.address.housenumber}
                            </td>
                            <td>{mansion.address.city}</td>
                            <td>{mansion.num_of_locations}</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );

    function toggleOne(id: number) {
        setFieldValue('mansionIds', toggleId(values.mansionIds, id)).then();
    }

    function toggleAll() {
        setFieldValue('mansionIds', allSelected ? [] : allIds).then();
    }
}

function toggleId(ids: number[], id: number) {
    if (!id) {
        return [id];
    }
    return ids.includes(id) ? ids.filter((it) => it !== id) : [...ids, id];
}

export default OperationalContactAssignmentsForm;
